import React, { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { useAuth } from '../contexts/AuthContext';
import { apiRequest } from '../config/api';
import toast from 'react-hot-toast';
import { CreditCard, Loader } from 'lucide-react';
import CouponField from './CouponField';
import './StripeCheckout.css';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const StripeCheckout = ({ plan, onSuccess, onCancel, appliedCoupon }) => {
  const [loading, setLoading] = useState(false);
  const [localAppliedCoupon, setLocalAppliedCoupon] = useState(appliedCoupon);
  const { currentUser } = useAuth();

  const handleCheckout = async () => {
    if (!currentUser) {
      toast.error('Debes iniciar sesión para continuar');
      return;
    }

    setLoading(true);

    try {
      // Crear sesión de checkout
      const endpoint = localAppliedCoupon ? '/api/coupons/apply' : '/api/payments/create-checkout-session';
      const body = localAppliedCoupon 
        ? { code: localAppliedCoupon.coupon.code, plan, userId: currentUser.uid }
        : { plan };
        
      const response = await apiRequest(endpoint, {
        method: 'POST',
        body
      });

      console.log('🔍 Respuesta del backend:', response);

      // Verificar si es modo mock
      if (response.sessionId && response.sessionId.startsWith('mock_session_')) {
        console.log('🔄 Modo mock detectado, simulando pago...');
        console.log('🎯 onSuccess function:', typeof onSuccess);
        toast.success('Simulando pago en modo desarrollo...');
        
        // Simular pago exitoso
        setTimeout(() => {
          console.log('⏰ Ejecutando onSuccess después de 2 segundos...');
          if (onSuccess) {
            console.log('✅ Llamando onSuccess function');
            onSuccess();
          } else {
            console.log('⚠️ onSuccess no está definido, redirigiendo manualmente');
            window.location.href = '/dashboard?payment=success&plan=' + plan + '&mock=true';
          }
        }, 2000);
        return;
      }

      // Modo Stripe real
      if (stripePromise && response.sessionId) {
        const stripe = await stripePromise;
        const { error } = await stripe.redirectToCheckout({
          sessionId: response.sessionId
        });

        if (error) {
          console.error('Error redirigiendo a checkout:', error);
          toast.error('Error al procesar el pago: ' + error.message);
        }
      } else {
        throw new Error('No se pudo crear la sesión de checkout');
      }

    } catch (error) {
      console.error('Error creando sesión de checkout:', error);
      toast.error('Error al procesar el pago: ' + (error.message || 'Error desconocido'));
    } finally {
      setLoading(false);
    }
  };

  const getPlanInfo = () => {
    const plans = {
      basico: {
        name: 'Plan Básico - RTB Collect',
        price: '$100 MXN',
        description: ''
      },
      premium: {
        name: 'Plan Premium - RTB COLLECT',
        price: '$200 MXN',
        description: ''
      },
      elite: {
        name: 'Plan Elite - RTB COLLECT',
        price: '$400 MXN',
        description: ''
      }
    };

    return plans[plan] || plans.basico;
  };

  const planInfo = getPlanInfo();

  return (
    <div className="stripe-checkout-container">
      <div className="checkout-card">
        <div className="checkout-header">
          <CreditCard size={24} className="checkout-icon" />
          <h3>Confirmar Pago</h3>
        </div>

        <div className="plan-summary">
          <h4>{planInfo.name}</h4>
          <div className="plan-price">{planInfo.price}</div>
          <p className="plan-description">{planInfo.description}</p>
        </div>

                 <div className="checkout-features">
           <h5>Incluye:</h5>
           <ul>
             <li>✅ Monitoreo automático de precios</li>
             <li>✅ Alertas por email</li>
             <li>✅ Dashboard personalizado</li>
             <li>✅ Soporte técnico</li>
           </ul>
         </div>

         {/* Campo de Cupón */}
         <div className="coupon-section">
           <h5>¿Tienes un código de descuento?</h5>
           <CouponField
             selectedPlan={plan}
             onCouponApplied={(couponData) => setLocalAppliedCoupon(couponData)}
             onCouponRemoved={() => setLocalAppliedCoupon(null)}
           />
         </div>

         <div className="checkout-actions">

                     <button
             className="checkout-button"
             onClick={(e) => {
               e.preventDefault();
               e.stopPropagation();
               handleCheckout();
             }}
            disabled={loading}
            style={{ 
              cursor: 'pointer',
              position: 'relative',
              zIndex: 1000,
              pointerEvents: 'auto'
            }}
          >
            {loading ? (
              <>
                <Loader size={20} className="spinner" />
                Procesando...
              </>
            ) : (
              <>
                <CreditCard size={20} />
                Pagar con Tarjeta
              </>
            )}
          </button>

          <button
            className="cancel-button"
            onClick={onCancel}
            disabled={loading}
          >
            Cancelar
          </button>
        </div>

        <div className="checkout-footer">
          <p className="security-note">
            🔒 Pago seguro procesado por Stripe
          </p>
          <p className="terms-note">
            Al continuar, aceptas nuestros{' '}
            <a href="/terms.html" target="_blank" rel="noopener noreferrer">
              Términos de Servicio
            </a>{' '}
            y{' '}
            <a href="/privacy.html" target="_blank" rel="noopener noreferrer">
              Política de Privacidad
            </a>
          </p>
          <p className="legal-disclaimer">
            <strong>Advertencia:</strong> Este servicio proporciona información de mercado únicamente con fines informativos. 
            No constituye asesoramiento financiero. Los usuarios son responsables de sus propias decisiones de inversión.
          </p>
        </div>
      </div>
    </div>
  );
};

export default StripeCheckout;
